/**
 * Hand-authored built-in tracks (a 9-hole round) showcasing the mechanics:
 * open greens, banked diagonal walls, ice, mud, water carries, speed arrows,
 * and tight obstacle navigation. Coordinates are tile (col,row) on a 49x25 grid.
 */
import { Surface, Shape, type Track } from '../types.js';
import { TrackBuilder } from './builder.js';

function h1(): Track {
  // The Opener — straight, gentle.
  return new TrackBuilder().border().tee(4, 12).hole(44, 12).build({ id: 'opener', name: 'The Opener', par: 2, difficulty: 'easy' });
}

function h2(): Track {
  // The Dogleg — bank off a diagonal wall.
  const b = new TrackBuilder().border();
  b.wall(20, 1, 6, 14); // vertical block from the top
  b.shaped(20, 15, Shape.TriangleNE); // smooth the inside corner
  b.tee(5, 5).hole(40, 19);
  return b.build({ id: 'dogleg', name: 'The Dogleg', par: 3, difficulty: 'easy', categories: ['traditional'] });
}

function h3(): Track {
  // Ice Rink — low friction, plan the carry.
  const b = new TrackBuilder().border();
  b.terrain(14, 4, 21, 17, Surface.Ice);
  b.wall(24, 9, 2, 7);
  b.tee(5, 12).hole(44, 12);
  return b.build({ id: 'icerink', name: 'Ice Rink', par: 3, difficulty: 'medium', categories: ['modern'] });
}

function h4(): Track {
  // Mudbath — heavy friction punishes overpowered shots near the cup.
  const b = new TrackBuilder().border();
  b.terrain(28, 3, 14, 19, Surface.Mud);
  b.tee(5, 12).hole(40, 12);
  return b.build({ id: 'mudbath', name: 'Mudbath', par: 3, difficulty: 'medium' });
}

function h5(): Track {
  // The Crossing — water carry with a narrow bridge.
  const b = new TrackBuilder().border();
  b.terrain(18, 1, 12, 10, Surface.Water);
  b.terrain(18, 14, 12, 10, Surface.Water);
  // bridge across the middle stays grass (rows 11-13)
  b.tee(6, 12).hole(42, 12);
  return b.build({ id: 'crossing', name: 'The Crossing', par: 3, difficulty: 'medium', categories: ['traditional'] });
}

function h6(): Track {
  // Bumpers — bouncy blocks pinball you around.
  const b = new TrackBuilder().border();
  b.set(16, 8, { special: null, shape: Shape.BigCircle, bg: Surface.Grass, fg: Surface.BouncyBlock });
  b.set(24, 16, { special: null, shape: Shape.BigCircle, bg: Surface.Grass, fg: Surface.BouncyBlock });
  b.set(32, 8, { special: null, shape: Shape.BigCircle, bg: Surface.Grass, fg: Surface.BouncyBlock });
  b.tee(5, 12).hole(44, 12);
  return b.build({ id: 'bumpers', name: 'Bumpers', par: 3, difficulty: 'hard', categories: ['modern'] });
}

function h7(): Track {
  // Fast Lane — speed arrows boost you through a gap.
  const b = new TrackBuilder().border();
  b.terrain(16, 11, 18, 3, Surface.SpeedE);
  b.wall(36, 1, 2, 9);
  b.wall(36, 15, 2, 9);
  b.tee(5, 12).hole(45, 12);
  return b.build({ id: 'fastlane', name: 'Fast Lane', par: 3, difficulty: 'hard', categories: ['modern'] });
}

function h8(): Track {
  // The Maze — thread the walls.
  const b = new TrackBuilder().border();
  b.wall(12, 1, 2, 16);
  b.wall(24, 8, 2, 16);
  b.wall(36, 1, 2, 16);
  b.tee(5, 12).hole(44, 6);
  return b.build({ id: 'maze', name: 'The Maze', par: 4, difficulty: 'hard', categories: ['long'] });
}

function h9(): Track {
  // Finale — diagonal banks + a central island.
  const b = new TrackBuilder().border();
  b.shaped(1, 1, Shape.TriangleSE);
  b.shaped(47, 1, Shape.TriangleSW);
  b.shaped(1, 23, Shape.TriangleNE);
  b.shaped(47, 23, Shape.TriangleNW);
  b.wall(23, 10, 4, 5);
  b.terrain(21, 8, 8, 1, Surface.Water);
  b.terrain(21, 16, 8, 1, Surface.Water);
  b.tee(5, 12).hole(44, 12);
  return b.build({ id: 'finale', name: 'Finale', par: 4, difficulty: 'hard', categories: ['holeinone', 'modern'] });
}

// ── Extra tracks (rotated into long rounds) ──────────────────────

function e1(): Track {
  // The Gate — one-way gates: easy going east, no way back west.
  const b = new TrackBuilder().border();
  b.wall(22, 1, 2, 23);
  b.terrain(22, 7, 2, 2, Surface.OneWayE);
  b.terrain(22, 12, 2, 2, Surface.OneWayE);
  b.terrain(22, 18, 2, 2, Surface.OneWayE);
  b.wall(34, 8, 2, 9); // post-gate obstacle so overshoots need a plan
  b.tee(5, 12).hole(43, 12);
  return b.build({ id: 'gate', name: 'The Gate', par: 3, difficulty: 'medium', categories: ['modern'] });
}

function e2(): Track {
  // Minefield — thread the mines (or gamble: craters are safe afterwards).
  const b = new TrackBuilder().border();
  b.special(18, 6, Surface.Mine);
  b.special(20, 16, Surface.Mine);
  b.special(23, 11, Surface.Mine);
  b.special(26, 4, Surface.Mine);
  b.special(27, 19, Surface.Mine);
  b.special(30, 9, Surface.Mine);
  b.special(33, 14, Surface.Mine);
  b.special(36, 7, Surface.Mine);
  b.special(25, 12, Surface.BigMine);
  b.tee(4, 12).hole(44, 12);
  return b.build({ id: 'minefield', name: 'Minefield', par: 4, difficulty: 'medium', categories: ['modern'] });
}

function e3(): Track {
  // Magnetize — attract fields bend the line; a repel guards the cup.
  const b = new TrackBuilder().border();
  b.special(24, 6, Surface.MagnetAttract);
  b.special(30, 18, Surface.MagnetAttract);
  b.special(40, 12, Surface.MagnetRepel);
  b.wall(14, 10, 2, 5);
  b.tee(4, 12).hole(44, 12);
  return b.build({ id: 'magnetize', name: 'Magnetize', par: 3, difficulty: 'hard', categories: ['modern'] });
}

function e4(): Track {
  // Demolition — smash through the cracked wall (the middle takes 4 hits).
  const b = new TrackBuilder().border();
  for (let r = 1; r < 24; r++) {
    const mid = r >= 10 && r <= 14;
    b.special(30, r, mid ? Surface.Breakable4 : Surface.Breakable1);
  }
  b.terrain(36, 1, 3, 8, Surface.Mud);
  b.tee(5, 12).hole(43, 12);
  return b.build({ id: 'demolition', name: 'Demolition', par: 4, difficulty: 'medium', categories: ['modern'] });
}

function e5(): Track {
  // Blockade — shove the moveable blocks aside (or take the long way round).
  const b = new TrackBuilder().border();
  b.wall(28, 9, 6, 1);
  b.wall(28, 15, 6, 1);
  b.special(30, 11, Surface.MoveableBlock);
  b.special(30, 12, Surface.MoveableBlock);
  b.special(30, 13, Surface.MoveableBlock);
  b.terrain(20, 1, 3, 6, Surface.Water);
  b.terrain(20, 19, 3, 5, Surface.Water);
  b.tee(5, 12).hole(43, 12);
  return b.build({ id: 'blockade', name: 'Blockade', par: 4, difficulty: 'hard', categories: ['modern'] });
}

function h10(): Track {
  // Hellmouth — every hazard at once for long-round finales.
  const b = new TrackBuilder().border();
  b.terrain(3, 2, 11, 7, Surface.Ice);
  b.terrain(3, 16, 12, 6, Surface.Mud);
  b.terrain(17, 1, 4, 23, Surface.Water);
  b.terrain(28, 1, 3, 18, Surface.Acid);
  b.terrain(32, 16, 10, 6, Surface.WaterSwamp);
  b.terrain(21, 10, 7, 3, Surface.SpeedE);
  b.terrain(31, 5, 8, 2, Surface.SpeedSE);
  b.terrain(39, 8, 3, 7, Surface.SpeedS);

  b.wall(10, 9, 2, 5);
  b.wall(14, 12, 5, 2);
  b.wall(24, 3, 2, 6);
  b.wall(35, 11, 2, 6);
  b.shaped(15, 14, Shape.TriangleNE);
  b.shaped(27, 9, Shape.TriangleSW);
  b.shaped(42, 15, Shape.TriangleNW);

  b.set(13, 7, { special: null, shape: Shape.BigCircle, bg: Surface.Ice, fg: Surface.BouncyBlock });
  b.set(25, 15, { special: null, shape: Shape.Diamond, bg: Surface.Grass, fg: Surface.StickyBlock });
  b.set(33, 8, { special: null, shape: Shape.BigCircle, bg: Surface.Grass, fg: Surface.BouncyBlock });
  b.special(22, 5, Surface.Mine);
  b.special(40, 18, Surface.BigMine);
  b.special(6, 20, Surface.TeleBlueIn);
  b.special(43, 4, Surface.TeleBlueOut);
  b.special(7, 4, Surface.TeleRedIn);
  b.special(34, 20, Surface.TeleRedOut);

  b.tee(4, 12).hole(45, 20);
  return b.build({ id: 'hellmouth', name: 'Hellmouth', par: 6, difficulty: 'hard', categories: ['long', 'modern'] });
}

export const STANDARD_TRACKS: Track[] = [h1(), h2(), h3(), h4(), h5(), h6(), h7(), h8(), h9()];
/** Extra tracks showcasing gates, mines, magnets, breakables and moveables. */
export const EXTRA_TRACKS: Track[] = [e1(), e2(), e3(), e4(), e5()];
export const HELL_TRACK: Track = h10();
export const BUILTIN_TRACKS: Track[] = [...STANDARD_TRACKS, ...EXTRA_TRACKS, HELL_TRACK];
